import { ChangeEvent, MouseEvent } from "react";
import { ProductListItem } from "../../../types/product.type";
import { formatDateTimeBkk } from "../../../utils/date";
import StatusDot from "../shared/StatusDot";
import {
  CATALOGUE_CELL_PADDING,
  STRIPE_BACKGROUND,
  formatCataloguePrice,
} from "../shared/constants";
import { resolveProductUnit } from "./productUnit";
import { WEB_IT_EXPORT_LABEL } from "./webItExport";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

const EMPTY_PLACEHOLDER = "-";

interface ProductRowProps {
  product: ProductListItem;
  /** Kept identical to the loading skeleton's rows so the table never resizes on load. */
  rowHeight: number;
  /** The product thumbnail is square; its edge in px. */
  thumbnailSize: number;
  striped: boolean;
  /** The category's display name, or "" when the product is filed under none. */
  categoryName: string;
  canEdit: boolean;
  /** The row's WEB_IT export flag is being saved, so it cannot be switched again yet. */
  isExportSaving: boolean;
  onToggleExport: (product: ProductListItem, nextAllowExport: boolean) => void;
  onView: (uuid: string) => void;
  onEdit: (uuid: string) => void;
  onDuplicate: (uuid: string) => void;
  onDelete: (product: ProductListItem) => void;
}

export default function ProductTableRow({
  product,
  rowHeight,
  thumbnailSize,
  striped,
  categoryName,
  canEdit,
  isExportSaving,
  onToggleExport,
  onView,
  onEdit,
  onDuplicate,
  onDelete,
}: ProductRowProps) {
  const unit = resolveProductUnit(product.units_json) ?? EMPTY_PLACEHOLDER;
  const facePrice = formatCataloguePrice(product.face_price);

  const handleView = () => onView(product.uuid);

  const handleViewFromButton = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onView(product.uuid);
  };

  const handleEdit = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onEdit(product.uuid);
  };

  const handleDuplicate = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDuplicate(product.uuid);
  };

  const handleDelete = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDelete(product);
  };

  // Switching the flag must not also open the product the row links to.
  const handleExportSwitchClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };

  const handleToggleExport = (
    _event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    onToggleExport(product, checked);
  };

  return (
    <TableRow
      sx={{
        bgcolor: striped ? STRIPE_BACKGROUND : "#fff",
        height: rowHeight,
        "& td, & th": { padding: `${CATALOGUE_CELL_PADDING}px` },
        cursor: "pointer",
      }}
      onClick={handleView}
    >
      <TableCell align="center">{product.sequence ?? "—"}</TableCell>
      <TableCell>
        <Stack direction="row" justifyContent="center">
          <Avatar
            src={product.thumbnail ?? undefined}
            alt={product.name}
            variant="rounded"
            sx={{ width: thumbnailSize, height: thumbnailSize }}
          />
        </Stack>
      </TableCell>
      <TableCell>{product.code}</TableCell>
      <TableCell>{product.name}</TableCell>
      <TableCell>{product.brand || EMPTY_PLACEHOLDER}</TableCell>
      <TableCell>{categoryName || EMPTY_PLACEHOLDER}</TableCell>
      <TableCell align="center">{unit}</TableCell>
      <TableCell align="right">{facePrice}</TableCell>
      <TableCell>{product.barcode1 || EMPTY_PLACEHOLDER}</TableCell>
      <TableCell>{product.barcode2 || EMPTY_PLACEHOLDER}</TableCell>
      <TableCell>{product.dimension_text || EMPTY_PLACEHOLDER}</TableCell>
      <TableCell>{product.fda_no || EMPTY_PLACEHOLDER}</TableCell>
      <TableCell>
        <StatusDot status={product.status} />
      </TableCell>
      <TableCell align="center">
        <Switch
          size="small"
          checked={product.allow_export}
          disabled={!canEdit || isExportSaving}
          onClick={handleExportSwitchClick}
          onChange={handleToggleExport}
          slotProps={{ input: { "aria-label": WEB_IT_EXPORT_LABEL } }}
        />
      </TableCell>
      <TableCell>
        {formatDateTimeBkk(product.updated_at) || EMPTY_PLACEHOLDER}
      </TableCell>
      <TableCell>
        <Stack direction="row" gap={0.5}>
          <Tooltip title="ดูรายละเอียด">
            <IconButton
              size="small"
              color="primary"
              onClick={handleViewFromButton}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canEdit && (
            <>
              <Tooltip title="แก้ไข">
                <IconButton size="small" color="primary" onClick={handleEdit}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="ทำสำเนา">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleDuplicate}
                >
                  <ContentCopyOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="ลบ">
                <IconButton size="small" color="error" onClick={handleDelete}>
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
}
